import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import MoneysaverPage from './pages/Moneysaver/MoneysaverPage';
import EnvironmentDetailPage from './pages/Moneysaver/EnvironmentDetailPage';
import LoginPage from './pages/Auth/LoginPage';
import UnauthorizedPage from './pages/Auth/UnauthorizedPage';
import LoadingScreen from './components/common/LoadingScreen';
import NotFoundPage from './pages/NotFoundPage';
import ApiService from './services/api';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/auth/PrivateRoute';
import './App.css';

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await ApiService.init();
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize application:', error);
        setError('Failed to load application configuration. Please try refreshing the page.');
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (isLoading) {
    return <LoadingScreen message="Loading application..." />;
  }

  if (error) {
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="p-6 max-w-sm mx-auto bg-white rounded-xl shadow-md flex items-center space-x-4">
            <div>
              <div className="text-xl font-medium text-red-600">Error</div>
              <p className="text-gray-500">{error}</p>
              <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Reload Application
              </button>
            </div>
          </div>
        </div>
    );
  }

  return (
      <Router>
        <AuthProvider>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Protected routes - require authentication */}
              <Route path="/" element={
                <PrivateRoute>
                  <>
                    <Navbar />
                    <main className="container mx-auto px-4 py-8">
                      <div className="bg-white rounded-lg shadow-sm p-6">
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">
                          AWS Utilities Dashboard
                        </h1>
                        <p className="text-gray-600">
                          Access and manage various AWS utility tools from one central location.
                        </p>
                      </div>
                    </main>
                  </>
                </PrivateRoute>
              } />

              <Route path="/moneysaver" element={
                <PrivateRoute>
                  <>
                    <Navbar />
                    <main className="container mx-auto px-4 py-8">
                      <MoneysaverPage />
                    </main>
                  </>
                </PrivateRoute>
              } />

              <Route path="/moneysaver/environment/:envName" element={
                <PrivateRoute>
                  <>
                    <Navbar />
                    <main className="container mx-auto px-4 py-8">
                      <EnvironmentDetailPage />
                    </main>
                  </>
                </PrivateRoute>
              } />

              {/* Admin routes - require super user role */}
              <Route path="/settings" element={
                <PrivateRoute superUserOnly={true}>
                  <>
                    <Navbar />
                    <main className="container mx-auto px-4 py-8">
                      <div className="bg-white rounded-lg shadow-sm p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Settings</h1>
                        <p className="text-gray-600">Manage application settings and user permissions.</p>
                      </div>
                    </main>
                  </>
                </PrivateRoute>
              } />

              {/* Catch all route - 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </AuthProvider>
      </Router>
  );
};

export default App;